import React, { useCallback, useMemo, useEffect, useRef, memo, useState } from "react";
import RichTextEditor, { BaseKit } from "reactjs-tiptap-editor";
import { debounce } from "lodash";
import Box from "@mui/material/Box";
import { locale } from "reactjs-tiptap-editor/locale-bundle";
import { Bold } from "reactjs-tiptap-editor/bold";
import { BulletList } from "reactjs-tiptap-editor/bulletlist";
import { Color } from "reactjs-tiptap-editor/color";
import { Document } from "reactjs-tiptap-editor/document";
import { FontFamily } from "reactjs-tiptap-editor/fontfamily";
import { FontSize } from "reactjs-tiptap-editor/fontsize";
import { FormatPainter } from "reactjs-tiptap-editor/formatpainter";
import { Heading } from "reactjs-tiptap-editor/heading";
import { Highlight } from "reactjs-tiptap-editor/highlight";
import { Image } from "reactjs-tiptap-editor/image";
import { Indent } from "reactjs-tiptap-editor/indent";
import { Italic } from "reactjs-tiptap-editor/italic";
import { LineHeight } from "reactjs-tiptap-editor/lineheight";
import { Link } from "reactjs-tiptap-editor/link";
import { ListItem } from "reactjs-tiptap-editor/listitem";
import { MoreMark } from "reactjs-tiptap-editor/moremark";
import { MultiColumn } from "reactjs-tiptap-editor/multicolumn";
import { OrderedList } from "reactjs-tiptap-editor/orderedlist";
import { Selection } from "reactjs-tiptap-editor/selection";
import { SlashCommand } from "reactjs-tiptap-editor/slashcommand";
import { Table } from "reactjs-tiptap-editor/table";
import { TaskList } from "reactjs-tiptap-editor/tasklist";
import { TextAlign } from "reactjs-tiptap-editor/textalign";
import { TextBubble } from "reactjs-tiptap-editor/textbubble";
import { TrailingNode } from "reactjs-tiptap-editor/trailingnode";
import { TextUnderline } from "reactjs-tiptap-editor/textunderline";
import { SearchAndReplace } from "reactjs-tiptap-editor/searchandreplace";
import { useMaterialUIController } from "context";
import { History } from "reactjs-tiptap-editor/history";
import { Mention } from "reactjs-tiptap-editor/Mention";
import i18n from "i18n";
import useGetImageUploaded from "hooks/useGetImageUploaded";
import localFr from "./localFr";
import suggestion from "../ConversationInput/Suggestion";
import useMapDictionary from "../../../hooks/useMapDictionary";
import "../ConversationInput/MentionList.css";
import "reactjs-tiptap-editor/style.css";
import "katex/dist/katex.min.css";
import "./style.css";
const EditorHtml = memo(
	({
		language,
		dictionary,
		onSetHtml,
		value,
		trigger,
		code,
		alternativeIndex,
		channelType,
		isTicket = false
	}) => {
		const { mappedDictionary, existingKeys } = useMapDictionary(dictionary);

		const memoizedExistingKeys = useMemo(() => existingKeys, [JSON.stringify(existingKeys)]);
		const editorRef = useRef();
		const [controller] = useMaterialUIController();
		const { darkMode } = controller;
		const [content, setContent] = useState("");

		locale.setMessage("fr", {
			...localFr
		});

		locale.setLang(i18n.language || "en");

		const uploadFile = useGetImageUploaded();
		const extensions = useMemo(() => {
			const configs = [
				BaseKit.configure({
					placeholder: { showOnlyCurrent: true },
					characterCount: { limit: 500_000 }
				}),
				History,
				Heading.configure({
					levels: [1, 2, 3, 4, 5, 6]
				}),
				TextAlign.configure({
					types: ["paragraph", "heading"],
					alignments: ["left", "center", "right", "justify"]
				}),
				Bold,
				TextUnderline,
				ListItem,
				BulletList,
				OrderedList,
				TaskList,
				TextBubble,
				Italic,
				LineHeight,

				Table.configure({
					resizable: true
				}),
				Link.configure({
					openOnClick: true,
					autolink: true
				}),
				Image.configure({
					inline: true,
					allowBase64: true,
					HTMLAttributes: {
						class: "tiptap-image",
						alt: "Uploaded image",
						title: "Editor image"
					},
					upload: file => uploadFile(file, code, language),
					resize: true,
					crop: true
				}),

				Color,
				Document,
				FontFamily.configure({
					fontFamily: ["Arial", "Helvetica", "Times New Roman", "Courier New"]
				}),
				FontSize,
				FormatPainter,
				Highlight,

				Indent,

				MoreMark,
				MultiColumn,

				Selection,
				SlashCommand,

				TrailingNode,

				SearchAndReplace
			];
			if (isTicket) {
				configs.push(
					Mention.configure({
						HTMLAttributes: { class: "mention" },
						deleteTriggerWithBackspace: true,
						suggestion: {
							...suggestion,
							items: () => mappedDictionary ?? []
						},
						renderLabel({ options, node }) {
							return `${node.attrs.id ?? options.suggestion.char}${node.attrs.label}`;
						}
					})
				);
			}

			return configs;
		}, []);

		// Content change handler with debouncing
		const debouncedOnSetHtml = useMemo(
			() =>
				debounce(value => {
					onSetHtml(value);
				}, 300), // 300ms delay - adjust as needed
			[onSetHtml]
		);

		const onChangeContent = useCallback(
			value => {
				// setContent(value);

				const newValue = value || "";
				const replacedValue = memoizedExistingKeys.reduce((acc, keyItem) => {
					return acc.replaceAll(
						`<span data-type="mention" class="mention" id="${keyItem.value}" label="">${keyItem.value}</span>`,
						`{{{ ${keyItem.variable} }}}`
					);
				}, newValue);

				if (replacedValue?.length > 7) {
					debouncedOnSetHtml(replacedValue);
				}
			},
			[alternativeIndex, trigger, debouncedOnSetHtml]
		);

		// Don't forget to cleanup on unmount
		useEffect(() => {
			return () => {
				debouncedOnSetHtml.cancel();
			};
		}, [debouncedOnSetHtml]);

		/**
		 * NOTE: Do not remove this useEffect & trigger from the dependency array
		 * It's used to rerender the component when the trigger changes
		 * => Used on AI generation, allow to rerender the component with the new content
		 */
		useEffect(() => {
			if (editorRef.current && editorRef.current?.editor?.commands) {
				if (isTicket) {
					const newValue = value || "";
					const replacedValue = memoizedExistingKeys.reduce((acc, keyItem) => {
						return acc.replaceAll(
							`{{{ ${keyItem.variable} }}}`,
							`<span data-type="mention" class="mention" id="${keyItem.value}" label="">${keyItem.value}</span>`
						);
					}, newValue);
					editorRef.current?.editor?.commands?.setContent(replacedValue);
				} else editorRef.current?.editor?.commands?.setContent(value);
			}
		}, [trigger, alternativeIndex]);

		return (
			<Box my={2} className="editor-html">
				<RichTextEditor
					ref={editorRef}
					output="html"
					content={content}
					onChangeContent={onChangeContent}
					extensions={extensions}
					disableBubble={false}
					hideBubble={false}
					hideToolbar={false}
					toolbarPosition="top"
					toolbarSticky={false}
					dark={darkMode ? true : false}
				/>
			</Box>
		);
	},
	(prevProps, nextProps) => {
		return prevProps.trigger === nextProps.trigger && prevProps.value === nextProps.value;
	}
);

export default EditorHtml;
